import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, FileText, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import {
  downloadConferencePdf,
  fetchAdminOrder,
  fetchExpeditionPricingPreview,
  transitionOrder,
  updateExpeditionListItem,
  type ExpeditionPricingPreview,
} from "@/lib/ordersApi";
import { fetchDriverOptions, type DriverOption } from "@/lib/adminDeliveryApi";
import { ApiError, getApiErrorMessage } from "@/lib/api";
import { formatDeliveryLabel, orderTitle } from "@/lib/orderUtils";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import type { FulfillmentMode, OrderDetail } from "@/lib/types";
import { ExpeditionConferenceList, type ExpeditionItemUpdate } from "@/components/admin/ExpeditionConferenceList";
import { AdvanceConfirmDialog } from "@/components/admin/AdvanceConfirmDialog";
import { FulfillmentModeToggle } from "@/components/admin/FulfillmentModeToggle";
import {
  ExpeditionConferenceFields,
  type ExpeditionConditionInfo,
  type ExpeditionConferenceValue,
} from "@/components/admin/ExpeditionConferenceFields";
import { fetchAdminCustomer } from "@/lib/adminCustomersApi";

const EMPTY_CONFERENCE: ExpeditionConferenceValue = { nfNumber: "", boletoConfirmed: false, boletoNote: "" };

function todayBusinessDate(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

interface Props {
  orderId: string | null;
  onClose: () => void;
  onOrderChanged?: (order: OrderDetail) => void;
}

/**
 * Janela dedicada do dashboard de expedição: confere os itens carregados e libera
 * o pedido para entrega — mesma validação e popup (data + motorista) do dashboard
 * de operações do admin.
 */
export function ExpeditionOrderDialog({ orderId, onClose, onOrderChanged }: Props) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advanceDate, setAdvanceDate] = useState(todayBusinessDate());
  const [fulfillmentMode, setFulfillmentMode] = useState<FulfillmentMode>("ENTREGA");
  const [driverId, setDriverId] = useState("");
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [advancing, setAdvancing] = useState(false);
  const [pendingItems, setPendingItems] = useState<string[] | null>(null);
  const [backOpen, setBackOpen] = useState(false);
  const [conference, setConference] = useState<ExpeditionConferenceValue>(EMPTY_CONFERENCE);
  const [condition, setCondition] = useState<ExpeditionConditionInfo | null>(null);
  const [pricing, setPricing] = useState<ExpeditionPricingPreview | null>(null);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      return;
    }
    setLoading(true);
    fetchAdminOrder(orderId)
      .then(setOrder)
      .catch((error) => toast.error(getApiErrorMessage(error, "Não foi possível carregar o pedido")))
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => {
    if (advanceOpen && drivers.length === 0) {
      fetchDriverOptions().then(setDrivers).catch(() => setDrivers([]));
    }
  }, [advanceOpen, drivers.length]);

  const applyUpdate = async (input: ExpeditionItemUpdate) => {
    if (!order) return;
    const updated = await updateExpeditionListItem(order.id, input);
    setOrder(updated);
    onOrderChanged?.(updated);
  };

  const openAdvance = () => {
    setAdvanceDate(todayBusinessDate());
    setFulfillmentMode("ENTREGA");
    setDriverId("");
    setPendingItems(null);
    setConference(EMPTY_CONFERENCE);
    setCondition(null);
    setPricing(null);
    setAdvanceOpen(true);
    if (order?.clientId) {
      fetchAdminCustomer(order.clientId)
        .then((c) => setCondition({
          paymentMethod: c.defaultPaymentMethod ?? null,
          paymentTermDays: c.defaultPaymentTermDays ?? null,
          installments: c.defaultInstallments ?? null,
        }))
        .catch(() => setCondition(null));
    }
    if (order?.id) {
      fetchExpeditionPricingPreview(order.id).then(setPricing).catch(() => setPricing(null));
    }
  };

  const submitAdvance = async () => {
    if (!order) return;
    if (!advanceDate) {
      toast.error("Informe a data da entrega");
      return;
    }
    if (fulfillmentMode === "ENTREGA" && !driverId) {
      toast.error("Selecione o motorista responsável pela entrega");
      return;
    }
    // NF opcional na conclusão da expedição (obrigatoriedade suspensa por período indeterminado).
    if (pricing && !pricing.allPriced) {
      toast.error("Há itens sem preço resolvível. Cadastre o preço na tabela do cliente antes de concluir.");
      return;
    }
    setAdvancing(true);
    try {
      const updated = await transitionOrder(order.id, {
        toStatus: "EM_ROTA",
        transitionDate: advanceDate,
        fulfillmentMode,
        driverId: fulfillmentMode === "ENTREGA" ? driverId : undefined,
        nfNumber: conference.nfNumber.trim(),
        boletoConfirmed: conference.boletoConfirmed,
        boletoNote: conference.boletoNote.trim() || undefined,
        expectedTotal: pricing?.totalRaw ?? undefined,
        expectedSignature: pricing?.conferenceSignature ?? undefined,
      });
      onOrderChanged?.(updated);
      toast.success(`${orderTitle(order)} liberado para entrega`);
      setAdvanceOpen(false);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        const payload = error.details as { error?: string; message?: string; items?: string[] } | null;
        if (payload?.error === "PENDING_ITEMS") {
          setPendingItems(payload.items ?? []);
          setAdvancing(false);
          return;
        }
        toast.error(payload?.message ?? error.message);
        setAdvancing(false);
        return;
      }
      toast.error(getApiErrorMessage(error, "Não foi possível liberar o pedido"));
    } finally {
      setAdvancing(false);
    }
  };

  const exportConferencePdf = async () => {
    if (!order) return;
    try {
      const { filename, blob } = await downloadConferencePdf(order.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const code = err instanceof Error ? err.message : "";
      if (code === "NO_CONFERENCE") {
        toast.error("Pedido sem conferência de expedição — disponível após concluir a expedição.");
      } else if (code === "FORBIDDEN_PDF") {
        toast.error("Sem permissão para o documento financeiro do pedido.");
      } else {
        toast.error("Não foi possível gerar o PDF da expedição");
      }
    }
  };

  // Retorno de etapa: traz o pedido de volta para a produção.
  const submitBackToProduction = async (date: string) => {
    if (!order) return;
    try {
      const updated = await transitionOrder(order.id, { toStatus: "EM_PRODUCAO", transitionDate: date });
      onOrderChanged?.(updated);
      toast.success(`${orderTitle(order)} voltou para produção`);
      setBackOpen(false);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível voltar o pedido para produção"));
    }
  };

  const list = order?.expeditionList ?? [];
  const loaded = list.filter((i) => i.status === "CARREGADO").length;
  const missing = list.filter((i) => i.status === "EM_FALTA").length;
  const canAdvance = order?.status === "EM_EXPEDICAO";

  return (
    <>
      <Dialog open={orderId != null} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="flex max-h-[88vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Conferência de expedição</DialogTitle>
            <DialogDescription>Confira os itens carregados e libere o pedido para entrega.</DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Carregando pedido...
            </div>
          )}

          {!loading && order && (
            <>
              <header className="shrink-0 border-b border-border px-6 pt-6 pb-4">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-display text-lg font-semibold">{orderTitle(order)}</span>
                  <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    {loaded}/{list.length} carregado{list.length === 1 ? "" : "s"}
                  </span>
                  {missing > 0 && (
                    <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700">
                      {missing} em falta
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {CLIENT_TYPE_LABELS[order.clientType]} · entrega{" "}
                  <strong className="font-medium text-foreground">{formatDeliveryLabel(order)}</strong>
                </div>
                <div className="mt-2">
                  <Button size="sm" variant="outline" onClick={() => void exportConferencePdf()}>
                    <FileText className="h-4 w-4" /> PDF da expedição
                  </Button>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                <ExpeditionConferenceList order={order} onUpdate={applyUpdate} collapsible={false} />
              </div>

              <footer className="flex shrink-0 items-center gap-2 border-t border-border bg-surface-elevated/40 px-6 py-3">
                {canAdvance && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-transparent bg-warning text-white hover:bg-warning/90 hover:text-white"
                    onClick={() => setBackOpen(true)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar para produção
                  </Button>
                )}
                {canAdvance && (
                  <Button variant="hero" size="sm" className="ml-auto" onClick={openAdvance}>
                    <ArrowRight className="h-4 w-4" />
                    Avançar para entrega
                  </Button>
                )}
              </footer>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={advanceOpen} onOpenChange={(open) => !open && !advancing && setAdvanceOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-4 w-4" /> Liberar para entrega
            </DialogTitle>
            <DialogDescription>
              {order ? orderTitle(order) : ""} — mercadoria conferida e liberada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Data da entrega</label>
              <DatePicker value={advanceDate} onChange={setAdvanceDate} />
            </div>
            <FulfillmentModeToggle value={fulfillmentMode} onChange={setFulfillmentMode} />
            {fulfillmentMode === "ENTREGA" ? (
              <div className="space-y-2">
                <label className="text-sm font-medium">Motorista responsável *</label>
                <Select value={driverId || undefined} onValueChange={setDriverId}>
                  <SelectTrigger>
                    <SelectValue placeholder={drivers.length === 0 ? "Carregando..." : "Selecione o motorista"} />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700">
                O cliente vai retirar na fábrica. O pedido fica em <strong>Em entrega</strong> sem motorista e você
                confirma a entrega manualmente quando ele buscar.
              </p>
            )}
            <ExpeditionConferenceFields value={conference} onChange={setConference} condition={condition} pricing={pricing} />
            {pendingItems && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800">
                <p className="font-medium">Existem itens não conferidos:</p>
                <ul className="mt-1 list-inside list-disc text-xs">
                  {pendingItems.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
                <p className="mt-1 text-xs">Marque cada item como carregado ou em falta antes de liberar.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvanceOpen(false)} disabled={advancing}>
              Cancelar
            </Button>
            <Button onClick={() => void submitAdvance()} disabled={advancing}>
              {advancing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdvanceConfirmDialog
        open={backOpen}
        actionLabel="Voltar para"
        toStatusLabel="Produção"
        pedidoLabel={order ? orderTitle(order) : ""}
        onCancel={() => setBackOpen(false)}
        onConfirm={submitBackToProduction}
      />
    </>
  );
}
